/**
 * Typed selectors over a normalized assurance assessment.
 *
 * Every selector reads the raw assessment source tolerantly and returns
 * `undefined` (or an empty list) when the API did not measure something.
 * Views render "Not measured" / "Requires validation" for those gaps —
 * the model never invents a value.
 */

import {
  asArray,
  asRecord,
  deriveDecision,
  readBoolean,
  readNumber,
  readString,
} from "./data";
import type { AssuranceAssessment, AssuranceDecision } from "./types";

type Raw = Record<string, unknown>;

export interface ScanScope {
  scanId?: string;
  trigger?: string;
  durationSeconds?: number;
  filesDiscovered?: number;
  filesAnalyzed?: number;
  filesExcluded?: number;
  coveragePercent?: number;
  executableLoc?: number;
  totalLoc?: number;
  manifests?: number;
  lockfiles?: number;
  containerfiles?: number;
  iacFiles?: number;
  gitCommitsAnalyzed?: number;
  historyCoverageMonths?: number;
  completedAt?: string;
  status?: string;
  measured?: {
    findingsAnalyzed?: number;
    filesWithFindings?: number;
    analysisCapabilities?: number;
    capabilityList: string[];
    dependencyComponents?: number;
    languagesDetected?: number;
    filesInRepository?: number;
    sourceFiles?: number;
    testFiles?: number;
    manifestFiles?: number;
    lockFiles?: number;
    iacFiles?: number;
    containerFiles?: number;
    configFiles?: number;
    directories?: number;
    repositorySizeKb?: number;
  };
  exclusions: { pattern?: string; files?: number; reason?: string }[];
  limitations: string[];
}

export function selectScan(assessment: AssuranceAssessment): ScanScope {
  const scan = asRecord(assessment.source.scan);
  return {
    scanId: readString(scan, ["scanId", "id"]),
    trigger: readString(scan, ["trigger", "scanType", "scan_type"]),
    durationSeconds: readNumber(scan, ["durationSeconds"]),
    filesDiscovered: readNumber(scan, ["filesDiscovered"]),
    filesAnalyzed: readNumber(scan, ["filesAnalyzed"]),
    filesExcluded: readNumber(scan, ["filesExcluded"]),
    coveragePercent: readNumber(scan, ["coveragePercent", "coverage"]),
    executableLoc: readNumber(scan, ["executableLoc"]),
    totalLoc: readNumber(scan, ["totalLoc"]),
    manifests: readNumber(scan, ["manifests"]),
    lockfiles: readNumber(scan, ["lockfiles"]),
    containerfiles: readNumber(scan, ["containerfiles"]),
    iacFiles: readNumber(scan, ["iacFiles"]),
    gitCommitsAnalyzed: readNumber(scan, ["gitCommitsAnalyzed"]),
    historyCoverageMonths: readNumber(scan, ["historyCoverageMonths"]),
    completedAt: readString(scan, ["completedAt", "completed", "createdAt"]),
    status: readString(scan, ["status"]),
    measured: (() => {
      const m = asRecord(scan.measured);
      if (!Object.keys(m).length) return undefined;
      return {
        findingsAnalyzed: readNumber(m, ["findingsAnalyzed"]),
        filesWithFindings: readNumber(m, ["filesWithFindings"]),
        analysisCapabilities: readNumber(m, ["analysisCapabilities"]),
        capabilityList: asArray(m.capabilityList)
          .map((c) => (typeof c === "string" ? c : undefined))
          .filter((c): c is string => Boolean(c)),
        dependencyComponents: readNumber(m, ["dependencyComponents"]),
        languagesDetected: readNumber(m, ["languagesDetected"]),
        filesInRepository: readNumber(m, ["filesInRepository"]),
        sourceFiles: readNumber(m, ["sourceFiles"]),
        testFiles: readNumber(m, ["testFiles"]),
        manifestFiles: readNumber(m, ["manifestFiles"]),
        lockFiles: readNumber(m, ["lockFiles"]),
        iacFiles: readNumber(m, ["iacFiles"]),
        containerFiles: readNumber(m, ["containerFiles"]),
        configFiles: readNumber(m, ["configFiles"]),
        directories: readNumber(m, ["directories"]),
        repositorySizeKb: readNumber(m, ["repositorySizeKb"]),
      };
    })(),
    exclusions: asArray(scan.exclusions).map((item) => {
      const record = asRecord(item);
      return {
        pattern: readString(record, ["pattern", "path"]),
        files: readNumber(record, ["files", "count"]),
        reason: readString(record, ["reason", "note"]),
      };
    }),
    limitations: asArray(scan.limitations)
      .map((item) => (typeof item === "string" ? item : readString(asRecord(item), ["detail", "note", "description"])))
      .filter((item): item is string => Boolean(item)),
  };
}

export interface FindingsSummary {
  total?: number;
  open?: number;
  bySeverity: Record<string, number>;
  byDomain: Record<string, number>;
  priorityEvidenceCount?: number;
  riskScore?: number;
  riskBand?: string;
  estimatedReachable?: number;
  currentEvidence?: number;
  mixedOrPrototype?: number;
}

function countMap(value: unknown): Record<string, number> {
  const record = asRecord(value);
  const out: Record<string, number> = {};
  for (const [key, count] of Object.entries(record)) {
    const parsed = typeof count === "number" ? count : Number(count);
    if (Number.isFinite(parsed)) out[key] = parsed;
  }
  return out;
}

export function selectFindingsSummary(
  assessment: AssuranceAssessment
): FindingsSummary {
  const summary = asRecord(assessment.source.findingsSummary);
  const bySeverity = countMap(
    Object.keys(asRecord(summary.openBySeverity)).length
      ? summary.openBySeverity
      : summary.bySeverity
  );
  return {
    total: readNumber(summary, ["total"]) ?? (assessment.findings.length || undefined),
    open: readNumber(summary, ["open", "openFindings"]),
    bySeverity,
    byDomain: countMap(summary.byDomain),
    priorityEvidenceCount: readNumber(summary, ["priorityEvidenceCount"]),
    riskScore: readNumber(summary, ["riskScore", "risk_score"]),
    riskBand: readString(summary, ["riskBand", "risk_band"]),
    estimatedReachable: readNumber(summary, ["estimatedReachable"]),
    currentEvidence: readNumber(summary, ["currentEvidence"]),
    mixedOrPrototype: readNumber(summary, ["mixedOrPrototype"]),
  };
}

export function selectDecision(assessment: AssuranceAssessment): AssuranceDecision {
  return deriveDecision(asArray(assessment.source.policies));
}

export type AssuranceScanState = "none" | "failed" | "running" | "completed";

/** Live-mode scan state, derived from the persisted latest-scan record. */
export function selectScanState(assessment: AssuranceAssessment): AssuranceScanState {
  const scan = selectScan(assessment);
  const status = (scan.status || "").toLowerCase();
  if (!scan.scanId || status === "not_measured") return "none";
  if (["error", "failed"].includes(status)) return "failed";
  if (["queued", "running", "pending", "in_progress"].includes(status)) return "running";
  return "completed";
}

export interface PolicyGate {
  id?: string;
  name?: string;
  status?: string;
  evidence?: string;
  owner?: string;
}

export function selectPolicies(assessment: AssuranceAssessment): PolicyGate[] {
  return asArray(assessment.source.policies).map((item) => {
    const record = asRecord(item);
    return {
      id: readString(record, ["id"]),
      name: readString(record, ["name", "title"]),
      status: readString(record, ["status", "state", "result"]),
      evidence: readString(record, ["evidence", "detail", "note"]),
      owner: readString(record, ["owner", "team"]),
    };
  });
}

export interface ArchitectureModel {
  style?: string;
  nodes: {
    id?: string;
    label?: string;
    type?: string;
    exposure?: string;
    stack?: string;
    notes?: string;
  }[];
  edges: {
    from?: string;
    to?: string;
    protocol?: string;
    data?: string;
    trustBoundary?: boolean;
  }[];
  attackSurfaces: {
    surface?: string;
    routes?: number;
    authModes?: number;
    priorityFindings: string[];
    notes?: string;
  }[];
}

export function selectArchitecture(assessment: AssuranceAssessment): ArchitectureModel {
  const architecture = asRecord(assessment.source.architecture);
  return {
    style: readString(architecture, ["style", "summary"]),
    nodes: asArray(architecture.nodes).map((item) => {
      const record = asRecord(item);
      return {
        id: readString(record, ["id"]),
        label: readString(record, ["label", "name"]),
        type: readString(record, ["type", "kind"]),
        exposure: readString(record, ["exposure"]),
        stack: readString(record, ["stack", "technology"]),
        notes: readString(record, ["notes", "description"]),
      };
    }),
    edges: asArray(architecture.edges).map((item) => {
      const record = asRecord(item);
      return {
        from: readString(record, ["from", "source"]),
        to: readString(record, ["to", "target"]),
        protocol: readString(record, ["protocol"]),
        data: readString(record, ["data", "payload", "description"]),
        trustBoundary: readBoolean(record, ["trustBoundary", "trust_boundary"]),
      };
    }),
    attackSurfaces: asArray(architecture.attackSurfaces).map((item) => {
      const record = asRecord(item);
      return {
        surface: readString(record, ["surface", "name", "label"]),
        routes: readNumber(record, ["routes", "routeCount"]),
        authModes: readNumber(record, ["authModes", "auth_modes"]),
        priorityFindings: asArray(record.priorityFindings)
          .map((finding) =>
            typeof finding === "string"
              ? finding
              : readString(asRecord(finding), ["id", "findingId"])
          )
          .filter((finding): finding is string => Boolean(finding)),
        notes: readString(record, ["notes", "description"]),
      };
    }),
  };
}

export interface CompositionModel {
  productionPercent?: number;
  testPercent?: number;
  infrastructurePercent?: number;
  generatedPercent?: number;
  documentationPercent?: number;
  services?: number;
  sharedPackages?: number;
  deployableImages?: number;
  databases?: number;
  eventTopics?: number;
  // File-tree composition (provider-measured file counts).
  totalFiles?: number;
  directories?: number;
  sourceFiles?: number;
  testFiles?: number;
  manifestFiles?: number;
  lockFiles?: number;
  iacFiles?: number;
  containerFiles?: number;
  configFiles?: number;
  docFiles?: number;
  repositorySizeKb?: number;
  largestDirectories: { path?: string; files?: number }[];
}

export interface CodeSizeModel {
  status?: string;
  codeLines?: number;
  totalLines?: number;
  files?: number;
  perKloc?: number;
  per10kLoc?: number;
  findingsInScope?: number;
}

/** Lines of code and the finding density derived from them. */
export function selectCodeSize(assessment: AssuranceAssessment): CodeSizeModel {
  const size = asRecord(assessment.source.codeSize);
  const density = asRecord(assessment.source.findingDensity);
  const programming = asRecord(size.programmingLanguages);
  return {
    status: readString(size, ["status"]),
    codeLines: readNumber(programming, ["code"]),
    totalLines: readNumber(asRecord(size.totals), ["total"]),
    files: readNumber(programming, ["files"]),
    perKloc: readNumber(density, ["perKloc"]),
    per10kLoc: readNumber(density, ["per10kLoc"]),
    findingsInScope: readNumber(density, ["findingsInScope"]),
  };
}

export function selectComposition(assessment: AssuranceAssessment): CompositionModel {
  const composition = asRecord(assessment.source.codeComposition);
  return {
    productionPercent: readNumber(composition, ["productionPercent"]),
    testPercent: readNumber(composition, ["testPercent"]),
    infrastructurePercent: readNumber(composition, ["infrastructurePercent"]),
    generatedPercent: readNumber(composition, ["generatedPercent"]),
    documentationPercent: readNumber(composition, ["documentationPercent"]),
    services: readNumber(composition, ["services"]),
    sharedPackages: readNumber(composition, ["sharedPackages"]),
    deployableImages: readNumber(composition, ["deployableImages", "images"]),
    databases: readNumber(composition, ["databases"]),
    eventTopics: readNumber(composition, ["eventTopics"]),
    totalFiles: readNumber(composition, ["totalFiles"]),
    directories: readNumber(composition, ["directories"]),
    sourceFiles: readNumber(composition, ["sourceFiles"]),
    testFiles: readNumber(composition, ["testFiles"]),
    manifestFiles: readNumber(composition, ["manifestFiles"]),
    lockFiles: readNumber(composition, ["lockFiles"]),
    iacFiles: readNumber(composition, ["iacFiles"]),
    containerFiles: readNumber(composition, ["containerFiles"]),
    configFiles: readNumber(composition, ["configFiles"]),
    docFiles: readNumber(composition, ["docFiles"]),
    repositorySizeKb: readNumber(composition, ["repositorySizeKb"]),
    largestDirectories: asArray(composition.largestDirectories).map((item) => {
      const record = asRecord(item);
      return { path: readString(record, ["path"]), files: readNumber(record, ["files"]) };
    }),
  };
}

export interface LanguageShare {
  name?: string;
  loc?: number;
  percent?: number;
  files?: number;
}

export function selectLanguages(assessment: AssuranceAssessment): LanguageShare[] {
  return asArray(assessment.source.languages).map((item) => {
    const record = asRecord(item);
    return {
      name: readString(record, ["name", "language"]),
      loc: readNumber(record, ["loc", "lines"]),
      percent: readNumber(record, ["percent", "share"]),
      files: readNumber(record, ["files", "fileCount"]),
    };
  });
}

export interface ScannerRun {
  component?: string;
  version?: string;
  mode?: string;
  started?: string;
  completed?: string;
  result?: string;
}

export function selectScannerProvenance(assessment: AssuranceAssessment): ScannerRun[] {
  return asArray(assessment.source.scannerProvenance).map((item) => {
    const record = asRecord(item);
    return {
      component: readString(record, ["component", "name", "tool"]),
      version: readString(record, ["version"]),
      mode: readString(record, ["mode", "evidenceMode"]),
      started: readString(record, ["started", "startedAt"]),
      completed: readString(record, ["completed", "completedAt"]),
      result: readString(record, ["result", "status"]),
    };
  });
}

export interface CapabilityRow {
  capability?: string;
  evidence?: string;
  status?: string;
  limitation?: string;
}

export interface PriorityQueueEntry {
  findingId: string;
  title: string;
  severity: string;
  domain: string;
  score: number;
  factors: { label: string; detail: string; weight: number }[];
  rationale?: string;
}

/**
 * The "fix these first" queue: findings ranked by demonstrated exploit signal
 * rather than severity alone, each carrying the factors that produced its
 * position so the ordering can be explained to a client.
 */
export function selectPriorityQueue(assessment: AssuranceAssessment): PriorityQueueEntry[] {
  return asArray(assessment.source.priorityQueue).reduce<PriorityQueueEntry[]>((acc, item) => {
    const record = asRecord(item);
    const findingId = readString(record, ["findingId", "id"]);
    if (!findingId) return acc;
    acc.push({
      findingId,
      title: readString(record, ["title"]) || findingId,
      severity: readString(record, ["severity"]) || "info",
      domain: readString(record, ["domain"]) || "",
      score: readNumber(record, ["score"]) ?? 0,
      factors: asArray(record.factors).reduce<PriorityQueueEntry["factors"]>((list, raw) => {
        const factor = asRecord(raw);
        const label = readString(factor, ["label"]);
        if (label) {
          list.push({
            label,
            detail: readString(factor, ["detail"]) || "",
            weight: readNumber(factor, ["weight"]) ?? 0,
          });
        }
        return list;
      }, []),
      rationale: readString(record, ["rationale"]),
    });
    return acc;
  }, []);
}

export interface ScanDeltaView {
  status: string;
  previousScanAt?: string;
  currentScanAt?: string;
  introduced: number;
  resolved: number;
  carriedOver: number;
}

/** What changed between this scan and the previous one. */
export function selectScanDelta(assessment: AssuranceAssessment): ScanDeltaView | null {
  const raw = asRecord(assessment.source.scanDelta);
  const status = readString(raw, ["status"]);
  if (!status) return null;
  return {
    status,
    previousScanAt: readString(raw, ["previousScanAt"]),
    currentScanAt: readString(raw, ["currentScanAt"]),
    introduced: readNumber(raw, ["introduced"]) ?? 0,
    resolved: readNumber(raw, ["resolved"]) ?? 0,
    carriedOver: readNumber(raw, ["carriedOver"]) ?? 0,
  };
}

export function selectCapabilities(assessment: AssuranceAssessment): CapabilityRow[] {
  return asArray(assessment.source.capabilityEvidenceMatrix).map((item) => {
    const record = asRecord(item);
    return {
      capability: readString(record, ["capability", "name"]),
      evidence: readString(record, ["evidence", "detail"]),
      status: readString(record, ["status", "state"]),
      limitation: readString(record, ["limitation", "caveat", "note"]),
    };
  });
}

const EVIDENCE_BACKED = new Set([
  "demonstrated",
  "measured",
  "validated",
  "complete",
  "pass",
  "passed",
]);

export function isEvidenceBacked(status: string | undefined): boolean {
  return EVIDENCE_BACKED.has((status || "").trim().toLowerCase());
}

export interface DependencyModel {
  components?: number;
  direct?: number;
  transitive?: number;
  outdated?: number;
  vulnerable?: number;
  critical?: number;
  high?: number;
  medium?: number;
  low?: number;
  fixAvailable?: number;
  blockedUpgrades?: number;
  ecosystems: Record<string, number>;
}

export function selectDependencies(assessment: AssuranceAssessment): DependencyModel {
  const summary = asRecord(assessment.source.dependencySummary);
  return {
    components: readNumber(summary, ["components", "componentCount"]),
    direct: readNumber(summary, ["direct"]),
    transitive: readNumber(summary, ["transitive"]),
    outdated: readNumber(summary, ["outdated"]),
    vulnerable: readNumber(summary, ["vulnerable", "vulnerableComponents"]),
    critical: readNumber(summary, ["critical"]),
    high: readNumber(summary, ["high"]),
    medium: readNumber(summary, ["medium"]),
    low: readNumber(summary, ["low"]),
    fixAvailable: readNumber(summary, ["fixAvailable"]),
    blockedUpgrades: readNumber(summary, ["blockedUpgrades"]),
    ecosystems: countMap(summary.ecosystems),
  };
}

export interface PackageRow {
  name?: string;
  version?: string;
  relationship?: string;
  license?: string;
  fixVersion?: string;
  risk?: string;
  usage?: string;
  blocker?: string;
  ecosystem?: string;
  vulnerabilities?: number;
  cves: string[];
}

export function selectPackages(assessment: AssuranceAssessment): PackageRow[] {
  return asArray(assessment.source.packages).map((item) => {
    const record = asRecord(item);
    return {
      name: readString(record, ["name"]),
      version: readString(record, ["version"]),
      relationship: readString(record, ["relationship"]),
      license: readString(record, ["license"]),
      fixVersion: readString(record, ["fixVersion"]),
      risk: readString(record, ["risk"]),
      usage: readString(record, ["usage"]),
      blocker: readString(record, ["blocker"]),
      ecosystem: readString(record, ["ecosystem"]),
      vulnerabilities: readNumber(record, ["vulnerabilities"]),
      cves: asArray(record.cves)
        .map((cve) => (typeof cve === "string" ? cve : undefined))
        .filter((cve): cve is string => Boolean(cve)),
    };
  });
}

export interface SbomModel {
  serialNumber?: string;
  format?: string;
  componentCount?: number;
  representedComponentCount?: number;
  completeness?: string;
  generatedAt?: string;
  repositoryCommit?: string;
  commitBound?: boolean;
  tools: { name?: string; version?: string }[];
}

export function selectSbom(assessment: AssuranceAssessment): SbomModel | undefined {
  const sbom = asRecord(assessment.source.sbom);
  if (!Object.keys(sbom).length) return undefined;
  const provenance = asRecord(sbom.provenance);
  const repository = asRecord(assessment.source.repository);
  const repositoryCommit = readString(provenance, ["repositoryCommit", "commit"]);
  const repoCommit = readString(repository, ["commit", "commitSha"]);
  return {
    serialNumber: readString(sbom, ["serialNumber"]),
    format: readString(sbom, ["format"]),
    componentCount: readNumber(sbom, ["componentCount"]),
    representedComponentCount: readNumber(sbom, ["representedComponentCount"]),
    completeness: readString(sbom, ["completeness"]),
    generatedAt: readString(provenance, ["generatedAt"]) || readString(sbom, ["generatedAt"]),
    repositoryCommit,
    commitBound: Boolean(
      repositoryCommit && repoCommit && repositoryCommit === repoCommit
    ),
    tools: asArray(provenance.tools).map((tool) => {
      if (typeof tool === "string") return { name: tool };
      const record = asRecord(tool);
      return {
        name: readString(record, ["name"]),
        version: readString(record, ["version"]),
      };
    }),
  };
}

export interface LicenseModel {
  summary: { family?: string; components?: number; percent?: number; action?: string }[];
  policy: { approved?: number; review?: number; denied?: number; unknown?: number };
  obligations: {
    component?: string;
    license?: string;
    distribution?: string;
    decision?: string;
  }[];
}

export function selectLicenses(assessment: AssuranceAssessment): LicenseModel {
  const licenses = asRecord(assessment.source.licenses);
  const policy = asRecord(licenses.policy);
  return {
    summary: asArray(licenses.summary).map((item) => {
      const record = asRecord(item);
      return {
        family: readString(record, ["family", "group", "name"]),
        components: readNumber(record, ["components", "count"]),
        percent: readNumber(record, ["percent"]),
        action: readString(record, ["action", "guidance"]),
      };
    }),
    policy: {
      approved: readNumber(policy, ["approved"]),
      review: readNumber(policy, ["review"]),
      denied: readNumber(policy, ["denied", "restricted"]),
      unknown: readNumber(policy, ["unknown"]),
    },
    obligations: asArray(licenses.obligations).map((item) => {
      const record = asRecord(item);
      return {
        component: readString(record, ["component", "package"]),
        license: readString(record, ["license"]),
        distribution: readString(record, ["distribution", "context"]),
        decision: readString(record, ["decision", "status"]),
      };
    }),
  };
}

export interface QualityModel {
  maintainabilityIndex?: number;
  testCoveragePercent?: number;
  duplicationPercent?: number;
  cyclomaticComplexityP95?: number;
  cognitiveComplexityP95?: number;
  codeSmells?: number;
  estimatedDebtDays?: number;
  debtRatioPercent?: number;
  limitation?: string;
  structural?: {
    openFindings?: number;
    filesWithFindings?: number;
    findingDensityPer100Files?: number;
    testToSourceRatioPercent?: number;
    hotspotConcentrationPercent?: number;
    sourceFiles?: number;
    testFiles?: number;
  };
  hotspots: {
    file?: string;
    loc?: number;
    cyclomatic?: number;
    cognitive?: number;
    churn90d?: number;
    defects12m?: number;
    owner?: string;
    findingCount?: number;
    criticalCount?: number;
    domains: string[];
  }[];
  debtEstimate?: {
    currency?: string;
    blendedDailyRate?: number;
    remediationCostLow?: number;
    remediationCostExpected?: number;
    remediationCostHigh?: number;
    rebuildCostLow?: number;
    rebuildCostExpected?: number;
    rebuildCostHigh?: number;
    model?: string;
    caveat?: string;
  };
}

export function selectQuality(assessment: AssuranceAssessment): QualityModel {
  const quality = asRecord(assessment.source.quality);
  const debt = asRecord(quality.debtEstimate);
  return {
    maintainabilityIndex: readNumber(quality, ["maintainabilityIndex"]),
    testCoveragePercent: readNumber(quality, ["testCoveragePercent"]),
    duplicationPercent: readNumber(quality, ["duplicationPercent"]),
    cyclomaticComplexityP95: readNumber(quality, ["cyclomaticComplexityP95"]),
    cognitiveComplexityP95: readNumber(quality, ["cognitiveComplexityP95"]),
    codeSmells: readNumber(quality, ["codeSmells"]),
    estimatedDebtDays: readNumber(quality, ["estimatedDebtDays", "debtDays"]),
    debtRatioPercent: readNumber(quality, ["debtRatioPercent"]),
    limitation: readString(quality, ["limitation"]),
    structural: (() => {
      const s = asRecord(quality.structural);
      if (!Object.keys(s).length) return undefined;
      return {
        openFindings: readNumber(s, ["openFindings"]),
        filesWithFindings: readNumber(s, ["filesWithFindings"]),
        findingDensityPer100Files: readNumber(s, ["findingDensityPer100Files"]),
        testToSourceRatioPercent: readNumber(s, ["testToSourceRatioPercent"]),
        hotspotConcentrationPercent: readNumber(s, ["hotspotConcentrationPercent"]),
        sourceFiles: readNumber(s, ["sourceFiles"]),
        testFiles: readNumber(s, ["testFiles"]),
      };
    })(),
    hotspots: asArray(quality.hotspots).map((item) => {
      const record = asRecord(item);
      return {
        file: readString(record, ["file", "path"]),
        loc: readNumber(record, ["loc"]),
        cyclomatic: readNumber(record, ["cyclomatic"]),
        cognitive: readNumber(record, ["cognitive"]),
        churn90d: readNumber(record, ["churn90d"]),
        defects12m: readNumber(record, ["defects12m"]),
        owner: readString(record, ["owner", "team"]),
        findingCount: readNumber(record, ["findingCount"]),
        criticalCount: readNumber(record, ["criticalCount"]),
        domains: asArray(record.domains)
          .map((domain) => (typeof domain === "string" ? domain : undefined))
          .filter((domain): domain is string => Boolean(domain)),
      };
    }),
    debtEstimate: Object.keys(debt).length
      ? {
          currency: readString(debt, ["currency"]),
          blendedDailyRate: readNumber(debt, ["blendedDailyRate"]),
          remediationCostLow: readNumber(debt, ["remediationCostLow"]),
          remediationCostExpected: readNumber(debt, ["remediationCostExpected"]),
          remediationCostHigh: readNumber(debt, ["remediationCostHigh"]),
          rebuildCostLow: readNumber(debt, ["rebuildCostLow"]),
          rebuildCostExpected: readNumber(debt, ["rebuildCostExpected"]),
          rebuildCostHigh: readNumber(debt, ["rebuildCostHigh"]),
          model: readString(debt, ["model"]),
          caveat: readString(debt, ["caveat"]),
        }
      : undefined,
  };
}

export interface HealthModel {
  periodMonths?: number;
  commits?: number;
  commitsBasis?: string;
  pullRequests?: number;
  medianLeadTimeHours?: number;
  medianReviewHours?: number;
  changeFailureRatePercent?: number;
  revertedCommits?: number;
  busFactor?: number;
  ownershipConcentrationPercent?: number;
  activeContributors?: number;
  contributors: {
    name?: string;
    commits?: number;
    ownershipPercent?: number;
    areas: string[];
  }[];
  monthlyHistory: { month?: string; commits?: number; contributors?: number }[];
  fragileAreas: {
    area?: string;
    primaryOwners?: number;
    secondaryOwners?: number;
    risk?: string;
  }[];
  limitation?: string;
}

export function selectHealth(assessment: AssuranceAssessment): HealthModel {
  const health = asRecord(assessment.source.developmentHealth);
  const rawContributors = asArray(health.contributors).map((item) => {
    const record = asRecord(item);
    return {
      name: readString(record, ["name"]),
      commits: readNumber(record, ["commits"]),
      ownershipPercent: readNumber(record, ["ownershipPercent"]),
      areas: asArray(record.areas)
        .map((area) => (typeof area === "string" ? area : undefined))
        .filter((area): area is string => Boolean(area)),
    };
  });
  // Per-contributor commit share is arithmetic on measured commit counts, so
  // derive it when the provider didn't supply it (manual:// repos) rather than
  // leaving the column blank. This is a measured value, not an inference.
  const totalContributorCommits = rawContributors.reduce(
    (sum, c) => sum + (c.commits ?? 0),
    0
  );
  const contributors =
    totalContributorCommits > 0
      ? rawContributors.map((c) => ({
          ...c,
          ownershipPercent:
            c.ownershipPercent ??
            (c.commits != null
              ? Math.round((c.commits / totalContributorCommits) * 1000) / 10
              : undefined),
        }))
      : rawContributors;
  return {
    periodMonths: readNumber(health, ["periodMonths"]),
    commits: readNumber(health, ["commits"]),
    commitsBasis: readString(health, ["commitsBasis"]),
    pullRequests: readNumber(health, ["pullRequests"]),
    medianLeadTimeHours: readNumber(health, ["medianLeadTimeHours"]),
    medianReviewHours: readNumber(health, ["medianReviewHours", "medianReviewLatencyHours"]),
    changeFailureRatePercent: readNumber(health, ["changeFailureRatePercent", "changeFailureSignalPercent"]),
    revertedCommits: readNumber(health, ["revertedCommits", "revertCommits"]),
    busFactor: readNumber(health, ["busFactor"]),
    ownershipConcentrationPercent: readNumber(health, ["ownershipConcentrationPercent"]),
    activeContributors: readNumber(health, ["activeContributors"]),
    contributors,
    monthlyHistory: asArray(health.monthlyHistory).map((item) => {
      const record = asRecord(item);
      return {
        month: readString(record, ["month"]),
        commits: readNumber(record, ["commits"]),
        contributors: readNumber(record, ["contributors"]),
      };
    }),
    fragileAreas: asArray(health.fragileAreas).map((item) => {
      const record = asRecord(item);
      return {
        area: readString(record, ["area", "name"]),
        primaryOwners: readNumber(record, ["primaryOwners"]),
        secondaryOwners: readNumber(record, ["secondaryOwners"]),
        risk: readString(record, ["risk"]),
      };
    }),
    limitation: readString(health, ["limitation"]),
  };
}

export interface BenchmarkModel {
  cohort?: string;
  nature?: string;
  overallPercentile?: number;
  metrics: {
    name?: string;
    value?: number;
    cohortMedian?: number;
    percentile?: number;
    direction?: string;
    unit?: string;
  }[];
  references: { title?: string; url?: string; retrieved?: string; use?: string }[];
}

/**
 * The views test `direction === "lower-better"`, the demo document uses exactly
 * that, and the live API emits `lower_is_better`. The mismatch made every live
 * metric fall through to the "Higher" branch, so a repository with more open
 * vulnerabilities than its cohort was shown, in success green, as beating it —
 * on a benchmark a due-diligence reader is meant to act on. Normalise here, at
 * the single boundary both shapes pass through.
 */
function normalizeDirection(raw: string | undefined): string | undefined {
  if (!raw) return raw;
  const v = raw.toLowerCase().replace(/[\s_]+/g, "-");
  if (v.includes("lower")) return "lower-better";
  if (v.includes("higher")) return "higher-better";
  return raw;
}

export function selectBenchmark(assessment: AssuranceAssessment): BenchmarkModel {
  const benchmark = asRecord(assessment.source.benchmark);
  return {
    cohort: readString(benchmark, ["cohort"]),
    nature: readString(benchmark, ["nature", "caveat"]),
    overallPercentile: readNumber(benchmark, ["overallPercentile", "percentile"]),
    metrics: asArray(benchmark.metrics).map((item) => {
      const record = asRecord(item);
      return {
        name: readString(record, ["name", "metric"]),
        value: readNumber(record, ["value"]),
        cohortMedian: readNumber(record, ["cohortMedian", "median"]),
        percentile: readNumber(record, ["percentile"]),
        direction: normalizeDirection(readString(record, ["direction"])),
        unit: readString(record, ["unit"]),
      };
    }),
    references: asArray(assessment.source.benchmarkReferences).map((item) => {
      const record = asRecord(item);
      return {
        title: readString(record, ["title"]),
        url: readString(record, ["url"]),
        retrieved: readString(record, ["retrieved"]),
        use: readString(record, ["use"]),
      };
    }),
  };
}

export interface ComplianceRow {
  framework?: string;
  controls?: string;
  status?: string;
  note?: string;
  evidence: string[];
}

export function selectCompliance(assessment: AssuranceAssessment): ComplianceRow[] {
  return asArray(assessment.source.complianceMappings).map((item) => {
    const record = asRecord(item);
    return {
      framework: readString(record, ["framework"]),
      controls: readString(record, ["controls"]),
      status: readString(record, ["status", "state"]),
      note: readString(record, ["note", "detail"]),
      evidence: asArray(record.evidence)
        .map((entry) => (typeof entry === "string" ? entry : undefined))
        .filter((entry): entry is string => Boolean(entry)),
    };
  });
}

export interface AuditDecisionRow {
  id?: string;
  findingId?: string;
  decision?: string;
  decidedAt?: string;
  expiresAt?: string;
  approver?: string;
  rationale?: string;
  compensatingControls?: string;
  status?: string;
}

export function selectAuditDecisions(
  assessment: AssuranceAssessment
): AuditDecisionRow[] {
  return asArray(assessment.source.auditDecisions).map((item) => {
    const record = asRecord(item);
    return {
      id: readString(record, ["id"]),
      findingId: readString(record, ["findingId", "finding_id"]),
      decision: readString(record, ["decision"]),
      decidedAt: readString(record, ["decidedAt", "decided_at"]),
      expiresAt: readString(record, ["expiresAt", "expires_at"]),
      approver: readString(record, ["approver"]),
      rationale: readString(record, ["rationale", "reason"]),
      compensatingControls: readString(record, ["compensatingControls"]),
      status: readString(record, ["status"]),
    };
  });
}

export interface CampaignModel {
  id?: string;
  objective?: string;
  budgetEstimate?: number;
  phases: {
    window?: string;
    outcomes: string[];
    effortDays?: number;
  }[];
  workstreams: {
    name?: string;
    owner?: string;
    findings: string[];
    success?: string;
  }[];
}

export function selectCampaign(assessment: AssuranceAssessment): CampaignModel {
  const campaign = asRecord(assessment.source.remediationCampaign);
  return {
    id: readString(campaign, ["id"]),
    objective: readString(campaign, ["objective", "goal"]),
    budgetEstimate: readNumber(campaign, ["budgetEstimate", "budget"]),
    phases: asArray(campaign.phases).map((item) => {
      const record = asRecord(item);
      return {
        window: readString(record, ["window", "phase"]),
        outcomes: asArray(record.outcomes)
          .map((outcome) => (typeof outcome === "string" ? outcome : undefined))
          .filter((outcome): outcome is string => Boolean(outcome)),
        effortDays: readNumber(record, ["effortDays"]),
      };
    }),
    workstreams: asArray(campaign.workstreams).map((item) => {
      const record = asRecord(item);
      return {
        name: readString(record, ["name"]),
        owner: readString(record, ["owner"]),
        findings: asArray(record.findings)
          .map((finding) =>
            typeof finding === "string"
              ? finding
              : readString(asRecord(finding), ["id"])
          )
          .filter((finding): finding is string => Boolean(finding)),
        success: readString(record, ["success", "successCriteria"]),
      };
    }),
  };
}

export interface SyntheticBoundary {
  isSynthetic?: boolean;
  statement?: string;
  evidenceBoundary?: string;
  parityCaveat?: string;
}

export function selectSyntheticBoundary(
  assessment: AssuranceAssessment
): SyntheticBoundary {
  const notice = asRecord(assessment.source.syntheticNotice);
  return {
    isSynthetic: readBoolean(notice, ["isSynthetic"]),
    statement: readString(notice, ["statement"]),
    evidenceBoundary: readString(notice, ["evidenceBoundary"]),
    parityCaveat: readString(notice, ["parityCaveat"]),
  };
}

export interface GlossaryEntry {
  term?: string;
  definition?: string;
}

export function selectGlossary(assessment: AssuranceAssessment): GlossaryEntry[] {
  return asArray(assessment.source.glossary).map((item) => {
    const record = asRecord(item);
    return {
      term: readString(record, ["term"]),
      definition: readString(record, ["definition"]),
    };
  });
}

export function selectDocumentControl(assessment: AssuranceAssessment): Raw {
  return asRecord(assessment.source.documentControl);
}
